import api from './api'
import type {
  AllocationResult,
  CorrectionsDiff,
  StoreReconciliation,
  UnallocatedWarning,
} from '@/types/payrollPaymaster'

export interface TipRun {
  id: number
  start_date: string
  end_date: string
  status: string
  settings_snapshot: Record<string, unknown>
  pools_snapshot: Array<{ store: string; date: string; amount: string }>
  created_at: string
  finalized_at?: string | null
  superseded_by_run_id?: number | null
}

export interface TipRunLine {
  id: number
  run_id: number
  store?: string | null
  date?: string | null
  homebase_employee_id?: string | null
  employee_name?: string | null
  role_name?: string | null
  role_group?: string | null
  total_hours?: string | null
  eligibility_pct: string
  eligible_hours?: string | null
  group_day_share?: string | null
  payout_amount?: string | null
}

export interface TipRunDetail {
  run: TipRun
  lines: TipRunLine[]
  payouts_by_location: Record<string, string>
  warnings: UnallocatedWarning[]
  reconciliation: Record<string, StoreReconciliation>
  sync_warnings?: string[]
  incomplete_roles?: string[]
}

export interface TipRunRecalculateResult {
  original: TipRunDetail
  corrected: TipRunDetail
  diff: CorrectionsDiff
}

export interface TipPayoutExportRow {
  store: string
  employee: string
  group: string
  hours: string
  eligible_hours: string
  payout: string
}

export function buildTipPayoutExportRows(lines: TipRunLine[]): TipPayoutExportRow[] {
  const grouped = new Map<
    string,
    {
      store: string
      employee: string
      group: string
      hours: number
      eligible_hours: number
      payout: number
    }
  >()

  for (const line of lines) {
    if (!line.store || !line.homebase_employee_id) continue
    const key = `${line.store}\0${line.homebase_employee_id}\0${line.role_group ?? ''}`
    const existing = grouped.get(key) ?? {
      store: line.store,
      employee: line.employee_name ?? line.homebase_employee_id,
      group: line.role_group ?? '',
      hours: 0,
      eligible_hours: 0,
      payout: 0,
    }
    existing.hours += Number(line.total_hours ?? 0)
    existing.eligible_hours += Number(line.eligible_hours ?? 0)
    existing.payout += Number(line.payout_amount ?? 0)
    grouped.set(key, existing)
  }

  return Array.from(grouped.values())
    .sort((a, b) => {
      const storeCmp = a.store.localeCompare(b.store)
      if (storeCmp !== 0) return storeCmp
      const employeeCmp = a.employee.localeCompare(b.employee)
      if (employeeCmp !== 0) return employeeCmp
      return a.group.localeCompare(b.group)
    })
    .map((row) => ({
      store: row.store,
      employee: row.employee,
      group: row.group,
      hours: row.hours.toFixed(4),
      eligible_hours: row.eligible_hours.toFixed(4),
      payout: row.payout.toFixed(2),
    }))
}

export async function createTipRun(startDate: string, endDate: string): Promise<TipRunDetail> {
  const response = await api.post<TipRunDetail>('/tips/runs', {
    start_date: startDate,
    end_date: endDate,
  })
  return response.data
}

export async function refreshTipRun(
  startDate: string,
  endDate: string,
  skipSync = false
): Promise<TipRunDetail> {
  const response = await api.post<TipRunDetail>('/tips/runs/refresh', {
    start_date: startDate,
    end_date: endDate,
    skip_sync: skipSync,
  })
  return response.data
}

export async function getTipRun(runId: number): Promise<TipRunDetail> {
  const response = await api.get<TipRunDetail>(`/tips/runs/${runId}`)
  return response.data
}

export async function patchTipRunOverrides(
  runId: number,
  payload: { line_ids?: number[]; employee_id?: string; eligibility_pct: string }
): Promise<TipRunDetail> {
  const response = await api.patch<TipRunDetail>(`/tips/runs/${runId}/overrides`, payload)
  return response.data
}

export async function finalizeTipRun(runId: number): Promise<TipRunDetail> {
  const response = await api.post<TipRunDetail>(`/tips/runs/${runId}/finalize`)
  return response.data
}

export async function recalculateTipRun(runId: number): Promise<TipRunRecalculateResult> {
  const response = await api.post<TipRunRecalculateResult>(`/tips/runs/${runId}/recalculate`)
  return response.data
}

export async function finalizeTipRunRevision(
  draftRunId: number,
  sourceRunId: number
): Promise<TipRunDetail> {
  const response = await api.post<TipRunDetail>(
    `/tips/runs/${draftRunId}/finalize-revision`,
    { source_run_id: sourceRunId }
  )
  return response.data
}

export type PayoutsByEmployeeRow = {
  store: string
  employee_id: string
  employee_name: string
  total: string
  hours: number
}

export function rollupPayoutsByEmployee(lines: TipRunLine[]): PayoutsByEmployeeRow[] {
  const grouped = new Map<
    string,
    { store: string; employee_id: string; employee_name: string; total: number; hours: number }
  >()
  for (const line of lines) {
    if (!line.store || !line.homebase_employee_id) continue
    const key = `${line.store}\0${line.homebase_employee_id}`
    const existing = grouped.get(key) ?? {
      store: line.store,
      employee_id: line.homebase_employee_id,
      employee_name: line.employee_name ?? line.homebase_employee_id,
      total: 0,
      hours: 0,
    }
    existing.total += Number(line.payout_amount ?? 0)
    existing.hours += Number(line.total_hours ?? 0)
    grouped.set(key, existing)
  }
  return Array.from(grouped.values())
    .sort((a, b) => a.store.localeCompare(b.store) || a.employee_name.localeCompare(b.employee_name))
    .map((row) => ({
      store: row.store,
      employee_id: row.employee_id,
      employee_name: row.employee_name,
      total: row.total.toFixed(2),
      hours: row.hours,
    }))
}

export function formatPayoutsByEmployeeClipboard(rows: PayoutsByEmployeeRow[]): string {
  const header = 'Employee\tStore\tHours\tPayout'
  const body = rows.map((row) => {
    const hours = Number(row.hours)
    const hoursText = Number.isFinite(hours) ? hours.toFixed(2) : String(row.hours ?? '')
    return `${row.employee_name}\t${row.store}\t${hoursText}\t${row.total}`
  })
  return [header, ...body].join('\n')
}

export type { AllocationResult }

export interface UnmappedRolesResponse {
  roles: string[]
}

export interface CredentialTestResponse {
  ok: boolean
  message?: string
}

export interface PaymasterSyncResult {
  window: { start: string; end: string }
  sections: Record<string, Record<string, unknown> | { error: string }>
}

export async function getUnmappedRoles(): Promise<UnmappedRolesResponse> {
  const response = await api.get<UnmappedRolesResponse>('/tips/unmapped-roles')
  return response.data
}

export async function testHomebaseApiCredentials(): Promise<CredentialTestResponse> {
  const response = await api.post<CredentialTestResponse>(
    '/tips/credentials/homebase_api/test'
  )
  return response.data
}

export async function testCloverApiCredentials(
  merchantId?: string
): Promise<CredentialTestResponse> {
  const response = await api.post<CredentialTestResponse>(
    '/tips/credentials/clover_api/test',
    merchantId ? { merchant_id: merchantId } : {}
  )
  return response.data
}

export async function syncPaymasterDatasets(
  datasets: string[]
): Promise<PaymasterSyncResult> {
  const response = await api.post<PaymasterSyncResult>('/tips/sync', { datasets })
  return response.data
}
